// KPI formula 파서: 수식 문자열을 AST로 변환

export type FormulaNode =
  | { type: 'number'; value: number }
  | { type: 'variable'; name: string }
  | { type: 'function'; name: string; args: FormulaNode[] }
  | { type: 'binary'; op: string; left: FormulaNode; right: FormulaNode }
  | { type: 'unary'; op: string; arg: FormulaNode }

// 토큰 타입 정의
const TOKEN_REGEX = /\s*(\d+\.\d+|\d+|\[.*?\]|[A-Za-z_][A-Za-z0-9_]*|[+\-*/(),])/y

interface Token {
  type: 'number' | 'variable' | 'function' | 'operator' | 'paren' | 'comma'
  value: string
}

function tokenize(formula: string): Token[] {
  const tokens: Token[] = []
  let m: RegExpExecArray | null
  let lastIndex = 0
  while ((m = TOKEN_REGEX.exec(formula))) {
    lastIndex = TOKEN_REGEX.lastIndex
    // m[0]은 공백 포함, m[1]은 capturing group (공백 제외)
    const raw = m[1]
    if (/^\d/.test(raw)) {
      tokens.push({ type: 'number', value: raw })
    } else if (/^\[.*\]$/.test(raw)) {
      tokens.push({ type: 'variable', value: raw.slice(1, -1) })
    } else if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(raw)) {
      tokens.push({ type: 'function', value: raw })
    } else if (/^[+\-*/]$/.test(raw)) {
      tokens.push({ type: 'operator', value: raw })
    } else if (/^[(),]$/.test(raw)) {
      if (raw === ',') tokens.push({ type: 'comma', value: raw })
      else tokens.push({ type: 'paren', value: raw })
    }
  }
  if (lastIndex < formula.length) {
    throw new Error('Unexpected token at: ' + formula.slice(lastIndex))
  }
  return tokens
}

// 파서 구현 (재귀 하향, 연산자 우선순위, 함수/변수/숫자/괄호 지원)
export function parseFormula(formula: string): FormulaNode {
  const tokens = tokenize(formula)
  let pos = 0

  function peek() {
    return tokens[pos]
  }
  function next() {
    return tokens[pos++]
  }
  function expect(type: string, value?: string) {
    const t = next()
    if (!t || t.type !== type || (value && t.value !== value))
      throw new Error('Expected ' + type + (value ? ':' + value : ''))
    return t
  }

  function parsePrimary(): FormulaNode {
    const t = peek()
    if (!t) throw new Error('Unexpected end')
    if (t.type === 'number') {
      next()
      return { type: 'number', value: parseFloat(t.value) }
    }
    if (t.type === 'variable') {
      next()
      return { type: 'variable', name: t.value }
    }
    if (t.type === 'function') {
      // function 타입 토큰을 만났을 때, 다음 토큰이 '('인지 확인
      const name = t.value
      next()

      // 다음 토큰이 '('이면 함수 호출, 아니면 변수로 처리
      if (peek() && peek().type === 'paren' && peek().value === '(') {
        // 함수 호출: func(expr, ...)
        expect('paren', '(')
        const args: FormulaNode[] = []
        if ((peek() && peek().type !== 'paren') || (peek() && peek().value !== ')')) {
          while (true) {
            args.push(parseExpr())
            if (peek() && peek().type === 'comma') {
              next()
            } else break
          }
        }
        expect('paren', ')')
        return { type: 'function', name, args }
      } else {
        // 변수로 처리
        return { type: 'variable', name }
      }
    }
    if (t.type === 'operator' && t.value === '-') {
      next()
      return { type: 'unary', op: '-', arg: parsePrimary() }
    }
    if (t.type === 'paren' && t.value === '(') {
      next()
      const node = parseExpr()
      expect('paren', ')')
      return node
    }
    throw new Error('Unexpected token: ' + t.type + ':' + t.value)
  }

  function parseMulDiv(): FormulaNode {
    let node = parsePrimary()
    while (peek() && peek().type === 'operator' && (peek().value === '*' || peek().value === '/')) {
      const op = next().value
      node = { type: 'binary', op, left: node, right: parsePrimary() }
    }
    return node
  }

  function parseAddSub(): FormulaNode {
    let node = parseMulDiv()
    while (peek() && peek().type === 'operator' && (peek().value === '+' || peek().value === '-')) {
      const op = next().value
      node = { type: 'binary', op, left: node, right: parseMulDiv() }
    }
    return node
  }

  function parseExpr(): FormulaNode {
    return parseAddSub()
  }

  const ast = parseExpr()
  if (pos < tokens.length) throw new Error('Unexpected token at end: ' + JSON.stringify(tokens[pos]))
  return ast
}

/**
 * AST 안의 모든 변수명을 (중복 제거하여) 수집한다.
 *
 * 정규식 기반 추출(예: `[a-zA-Z_]\w*`)은 `[한글 변수명]`·공백 포함 이름을 놓치지만,
 * 파서가 만든 AST 의 variable 노드는 대괄호가 벗겨진 실제 이름을 담으므로 정확하다.
 * aggregate 등에서 "이 수식이 참조하는 metric 이름들"을 얻는 신뢰 가능한 경로.
 */
export function collectVariableNames(node: FormulaNode): string[] {
  const out = new Set<string>()
  const walk = (n: FormulaNode): void => {
    switch (n.type) {
      case 'variable':
        out.add(n.name)
        break
      case 'function':
        n.args.forEach(walk)
        break
      case 'binary':
        walk(n.left)
        walk(n.right)
        break
      case 'unary':
        walk(n.arg)
        break
    }
  }
  walk(node)
  return Array.from(out)
}

/**
 * AST 안의 모든 함수명을 (중복 제거하여) 수집한다.
 * formula 검증에서 "알 수 없는 함수" 를 잡는 데 쓴다.
 */
export function collectFunctionNames(node: FormulaNode): string[] {
  const out = new Set<string>()
  const walk = (n: FormulaNode): void => {
    switch (n.type) {
      case 'function':
        out.add(n.name)
        n.args.forEach(walk)
        break
      case 'binary':
        walk(n.left)
        walk(n.right)
        break
      case 'unary':
        walk(n.arg)
        break
    }
  }
  walk(node)
  return Array.from(out)
}
